
'use client';

import { useState, useEffect, useRef } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { List } from 'lucide-react';
import { type Heading } from './MarkdownRenderer';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

interface BlogTableOfContentsProps {
  headings: Heading[];
}

export default function BlogTableOfContents({ headings }: BlogTableOfContentsProps) {
  const isMobile = useIsMobile(1024); // Use lg breakpoint (1024px)
  const [activeId, setActiveId] = useState<string>('');
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (observer.current) {
      observer.current.disconnect();
    }

    observer.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '0% 0% -80% 0%' } // Triggers when heading is in the top 20% of the viewport
    );

    const elements = document.querySelectorAll('#article-content h2');
    elements.forEach((el) => observer.current?.observe(el));

    return () => observer.current?.disconnect();
  }, [headings]); // Rerun when headings change

  const handleLinkClick = () => {
    setIsSheetOpen(false);
  };

  const tocContent = (
    <nav>
      <ul>
        {headings.map((heading) => (
          <li key={heading.slug} className="mb-2">
            <a
              href={`#${heading.slug}`}
              onClick={handleLinkClick}
              className={cn(
                'block text-sm transition-all duration-200 ease-in-out pl-4 py-1 border-l-2',
                activeId === heading.slug
                  ? 'text-primary font-semibold border-primary'
                  : 'text-muted-foreground hover:text-foreground border-border'
              )}
            >
              {heading.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );

  if (headings.length === 0) {
    return null; // Don't render anything if there are no H2 headings
  }

  if (isMobile) {
    return (
      <>
        <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full shadow-lg lg:hidden bg-background/80 backdrop-blur-md"
              aria-label="Inhaltsverzeichnis öffnen"
            >
              <List className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[300px] bg-background">
            <SheetHeader className="mb-4 text-left">
              <SheetTitle>Inhaltsverzeichnis</SheetTitle>
            </SheetHeader>
            {tocContent}
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <aside className="sticky top-24 hidden lg:block">
      <h3 className="text-lg font-semibold text-foreground mb-4">In diesem Artikel</h3>
      {tocContent}
    </aside>
  );
}
